<template>
  <section
    class="card details-card"
    :class="{ 'details-card--compact': redirectButton }"
  >
    <div class="status-card">
      <div class="status-actions">
        <div class="status-main-actions">
          <woot-button
            color-scheme="success"
            size="small"
            :is-disabled="!canMarkWon || !canWriteOpportunities"
            @click="$emit('open-status-modal', 'won')"
          >
            {{ $t('OPPORTUNITY_DETAILS.ACTIONS.MARK_WON') }}
          </woot-button>
          <woot-button
            color-scheme="alert"
            size="small"
            :is-disabled="!canMarkLost || !canWriteOpportunities"
            @click="$emit('open-status-modal', 'lost')"
          >
            {{ $t('OPPORTUNITY_DETAILS.ACTIONS.MARK_LOST') }}
          </woot-button>
        </div>
        <button
          class="status-cancel-link"
          type="button"
          :disabled="isCancelDisabled"
          :class="{
            'status-cancel-link--disabled': isCancelDisabled,
          }"
          @click="$emit('open-status-modal', 'canceled')"
        >
          {{ $t('OPPORTUNITY_DETAILS.ACTIONS.CANCEL') }}
        </button>
      </div>
    </div>

    <opportunity-temperature-select
      v-if="opportunity"
      class="details-temperature"
      :value="displayedTemperature"
      :disabled="isTemperatureDisabled"
      :disabled-reason="temperatureDisabledReason"
      @input="onSelectTemperature"
    />

    <div class="details-header">
      <p class="section-title">{{ $t('OPPORTUNITY_DETAILS.DETAILS') }}</p>
      <router-link
        v-if="redirectButton"
        :to="opportunityRoute()"
        target="_blank"
        class="detail-redirect"
      >
        <fluent-icon icon="redirect" size="19" view-box="0 0 38 38" />
      </router-link>
      <woot-button
        v-else
        size="small"
        variant="smooth"
        color-scheme="secondary"
        icon="edit"
        class="edit-icon-button"
        :is-disabled="isEditLocked"
        :title="editButtonTitle"
        @click="$emit('open-edit-modal')"
      />
    </div>

    <div class="detail-lines">
      <div class="detail-summary">
        <span class="contact-id">#{{ opportunity ? opportunity.id : '' }}</span>
        <span class="contact-name" :title="contactDisplayName">
          {{ contactDisplayName }}
        </span>
        <span class="contact-meta">
          {{
            (opportunity &&
              opportunity.contact &&
              opportunity.contact.phone_number) ||
              '-'
          }}
        </span>
      </div>
      <div class="opportunity-main-row">
        <p class="opportunity-main-name" :title="opportunityName">
          {{ opportunityName }}
        </p>
        <p class="opportunity-main-value">{{ formattedValue }}</p>
      </div>
      <p>
        <strong>{{ $t('OPPORTUNITY_DETAILS.FIELDS.STAGE') }}:</strong>
        {{
          (opportunity &&
            opportunity.pipeline_stage &&
            opportunity.pipeline_stage.name) ||
            '-'
        }}
      </p>
      <p>
        <strong>{{ $t(descriptionFieldLabel) }}:</strong>
        {{ (opportunity && opportunity.description) || '-' }}
      </p>
      <p v-if="opportunity && opportunity.conversation">
        <strong>{{ $t(sourceConversationFieldLabel) }}:</strong>
        <router-link :to="sourceConversationRoute">
          {{
            $t('OPPORTUNITY_DETAILS.SOURCE_CONVERSATION_LINK', {
              id: opportunity.conversation.id,
            })
          }}
        </router-link>
      </p>
      <p>
        <strong>{{ $t('OPPORTUNITY_DETAILS.FIELDS.PRODUCTS') }}:</strong>
      </p>
      <div v-if="isLoadingProducts" class="details-products details-loading">
        <spinner size="small" color-scheme="dark" />
      </div>
      <opportunity-products-select
        v-else
        class="details-products"
        :selected-products="productPills"
        :available-product-labels="availableProductLabels"
        :disabled="isProductsDisabled || isEditLocked"
        @add="onAddProduct"
        @remove="onRemoveProduct"
      />

      <p>
        <strong>{{ $t('OPPORTUNITY_DETAILS.FIELDS.OWNER') }}:</strong>
      </p>
      <multiselect-dropdown
        v-if="canChangeOwner"
        :options="ownerPickerOptions"
        :selected-item="opportunity.owner || {}"
        :multiselector-title="$t('OPPORTUNITY_DETAILS.OWNER.SELECT_TITLE')"
        :multiselector-placeholder="
          $t('OPPORTUNITY_DETAILS.OWNER.SELECT_PLACEHOLDER')
        "
        :no-search-result="$t('OPPORTUNITY_DETAILS.OWNER.NO_RESULTS')"
        :input-placeholder="$t('OPPORTUNITY_DETAILS.OWNER.SEARCH_PLACEHOLDER')"
        @click="onSelectOwner"
      />
      <div v-else class="owner-card">
        <img
          v-if="ownerAvatarUrl"
          :src="ownerAvatarUrl"
          :alt="ownerName"
          class="owner-avatar"
        />
        <div v-else class="owner-avatar owner-avatar--fallback">
          {{ ownerInitial }}
        </div>
        <div class="owner-text">
          <p class="owner-name">{{ ownerName }}</p>
          <p class="owner-email">{{ ownerEmail }}</p>
        </div>
      </div>

      <button
        v-if="negotiationFormEnabled"
        type="button"
        class="negotiation-form-summary"
        @click="$emit('open-negotiation-form')"
      >
        <fluent-icon icon="document" size="32" class="negotiation-form-icon" />
        <span>{{ negotiationFormSummary }}</span>
      </button>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import opportunityWritePermission from 'dashboard/mixins/opportunityWritePermission';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Spinner from 'shared/components/Spinner';
import OpportunityProductsSelect from './OpportunityProductsSelect';
import OpportunityTemperatureSelect from './OpportunityTemperatureSelect.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import { DEFAULT_OPPORTUNITY_TEMPERATURE } from 'dashboard/helper/opportunityTemperatureHelper';

export default {
  name: 'OpportunityDetailsCard',
  components: {
    Spinner,
    OpportunityProductsSelect,
    OpportunityTemperatureSelect,
    MultiselectDropdown,
  },
  mixins: [alertMixin, opportunityWritePermission],
  props: {
    opportunity: {
      type: Object,
      default: null,
    },
    products: {
      type: Array,
      default: () => [],
    },
    canMarkWon: {
      type: Boolean,
      default: false,
    },
    canMarkLost: {
      type: Boolean,
      default: false,
    },
    canCancel: {
      type: Boolean,
      default: false,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    redirectButton: {
      type: Boolean,
      default: false,
    },
    pipelineUsers: {
      type: Array,
      default: () => [],
    },
    isLoadingProducts: {
      type: Boolean,
      default: false,
    },
    negotiationFormEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSubmittingOwner: false,
      isSubmittingTemperature: false,
      pendingTemperature: null,
    };
  },
  computed: {
    ...mapGetters({
      accountProductLabels: 'productLabels/getProductLabels',
      uiFlags: 'opportunities/getUIFlags',
    }),
    isEditLocked() {
      return this.isLocked || !this.canWriteOpportunities;
    },
    isCancelDisabled() {
      return !this.canCancel || !this.canWriteOpportunities;
    },
    editButtonTitle() {
      if (this.isLocked) {
        return this.$t('OPPORTUNITY_DETAILS.LOCKED.EDIT_DISABLED');
      }
      if (!this.canWriteOpportunities) {
        return this.$t('OPPORTUNITY_DETAILS.LOCKED.READ_ONLY');
      }
      return this.$t('OPPORTUNITY_DETAILS.EDIT');
    },
    displayedTemperature() {
      return (
        this.pendingTemperature ||
        (this.opportunity && this.opportunity.temperature) ||
        DEFAULT_OPPORTUNITY_TEMPERATURE
      );
    },
    isTemperatureDisabled() {
      return !this.opportunity || this.isEditLocked;
    },
    temperatureDisabledReason() {
      if (this.isLocked) {
        return this.$t('OPPORTUNITY_DETAILS.TEMPERATURE.LOCKED');
      }
      if (!this.canWriteOpportunities) {
        return this.$t('OPPORTUNITY_DETAILS.TEMPERATURE.NOT_ALLOWED');
      }
      return '';
    },
    isSubmittingProduct() {
      return this.uiFlags.isCreatingProduct || this.uiFlags.isDeletingProduct;
    },
    isOpportunityNotOpen() {
      return !!this.opportunity && this.opportunity.status !== 'open';
    },
    isProductsDisabled() {
      return this.isOpportunityNotOpen || this.isSubmittingProduct;
    },
    productPills() {
      return this.products.map(product => ({
        id: product.id,
        title:
          product.product_name ||
          (product.product_label && product.product_label.title) ||
          '-',
        description: product.product_label && product.product_label.description,
        color: product.product_label && product.product_label.color,
      }));
    },
    availableProductLabels() {
      const selectedProductLabelIds = this.products
        .map(product => product.product_label && product.product_label.id)
        .filter(Boolean);

      return this.accountProductLabels.filter(
        productLabel => !selectedProductLabelIds.includes(productLabel.id)
      );
    },
    contactDisplayName() {
      return (
        (this.opportunity &&
          this.opportunity.contact &&
          this.opportunity.contact.name) ||
        this.opportunityName
      );
    },
    opportunityName() {
      return (this.opportunity && this.opportunity.name) || '-';
    },
    formattedValue() {
      if (!this.opportunity) {
        return '-';
      }

      const value = Number(this.opportunity.value || 0);
      return `${this.opportunity.currency || 'BRL'} ${value.toLocaleString(
        'pt-BR',
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      )}`;
    },
    negotiationFormSummary() {
      const updatedAt =
        this.opportunity && this.opportunity.negotiation_form_updated_at;
      if (!updatedAt) {
        return this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.NOT_FILLED');
      }

      return this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.UPDATED_AT', {
        date: new Date(updatedAt).toLocaleDateString('pt-BR'),
      });
    },
    descriptionFieldLabel() {
      return 'OPPORTUNITY_DETAILS.FIELDS.DESCRIPTION';
    },
    sourceConversationFieldLabel() {
      return 'OPPORTUNITY_DETAILS.FIELDS.SOURCE_CONVERSATION';
    },
    sourceConversationRoute() {
      const accountId = this.$route.params.accountId;
      return frontendURL(
        conversationUrl({ accountId, id: this.opportunity.conversation.id })
      );
    },
    ownerName() {
      return (
        (this.opportunity &&
          this.opportunity.owner &&
          this.opportunity.owner.name) ||
        '-'
      );
    },
    ownerEmail() {
      return (
        (this.opportunity &&
          this.opportunity.owner &&
          this.opportunity.owner.email) ||
        '-'
      );
    },
    ownerAvatarUrl() {
      return (
        (this.opportunity &&
          this.opportunity.owner &&
          this.opportunity.owner.thumbnail) ||
        ''
      );
    },
    ownerInitial() {
      const name = this.ownerName;
      if (!name || name === '-') {
        return '?';
      }

      return String(name)
        .charAt(0)
        .toUpperCase();
    },
    canChangeOwner() {
      return !this.redirectButton && !this.isEditLocked && !!this.opportunity;
    },
    ownerPickerOptions() {
      const hasOwner = !!(this.opportunity && this.opportunity.owner);
      const noneOption = {
        id: 0,
        name: this.$t('OPPORTUNITY_DETAILS.OWNER.NONE'),
        email: '',
      };
      return [...(hasOwner ? [noneOption] : []), ...this.pipelineUsers];
    },
  },
  mounted() {
    if (!this.accountProductLabels.length) {
      this.$store.dispatch('productLabels/get');
    }
  },
  methods: {
    opportunityRoute() {
      return {
        name: 'opportunities_dashboard',
        params: {
          accountId: this.$route.params.accountId,
          opportunityId: this.opportunity.id,
        },
      };
    },
    async onAddProduct(productLabel) {
      if (!this.opportunity || this.isProductsDisabled) {
        return;
      }

      try {
        await this.$store.dispatch('opportunities/createProduct', {
          opportunityId: this.opportunity.id,
          productLabelId: productLabel.id,
        });
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DETAILS.ERRORS.ADD_PRODUCT')
        );
      } finally {
        this.$emit('refresh-products');
      }
    },
    async onSelectOwner(agent) {
      if (!this.opportunity || this.isSubmittingOwner) {
        return;
      }
      const currentOwnerId = this.opportunity.owner?.id || 0;
      const newOwnerId = agent && agent.id ? agent.id : 0;
      if (newOwnerId === currentOwnerId) {
        return;
      }

      this.isSubmittingOwner = true;
      try {
        await this.$store.dispatch('opportunities/assignOwner', {
          opportunityId: this.opportunity.id,
          ownerId: newOwnerId || null,
        });
        this.$emit('owner-changed');
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DETAILS.ERRORS.UPDATE')
        );
      } finally {
        this.isSubmittingOwner = false;
      }
    },
    async onSelectTemperature(temperature) {
      if (
        !this.opportunity ||
        this.isTemperatureDisabled ||
        this.isSubmittingTemperature
      ) {
        return;
      }

      this.pendingTemperature = temperature;
      this.isSubmittingTemperature = true;
      try {
        const opportunity = await this.$store.dispatch(
          'opportunities/updateTemperature',
          {
            opportunityId: this.opportunity.id,
            temperature,
          }
        );
        this.$emit('temperature-changed', opportunity);
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DETAILS.ERRORS.TEMPERATURE')
        );
      } finally {
        this.pendingTemperature = null;
        this.isSubmittingTemperature = false;
      }
    },
    async onRemoveProduct(product) {
      if (!this.opportunity || this.isProductsDisabled) {
        return;
      }

      try {
        await this.$store.dispatch('opportunities/deleteProduct', {
          opportunityId: this.opportunity.id,
          productId: product.id,
        });
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DETAILS.ERRORS.REMOVE_PRODUCT')
        );
      } finally {
        this.$emit('refresh-products');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  background: var(--s-25);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-medium);
  margin-bottom: var(--space-medium);
  padding: var(--space-medium);
}

.section-title {
  color: var(--s-900);
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-normal);
}

.details-temperature {
  margin-bottom: var(--space-normal);
}

.details-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-small);
}

.details-header .section-title {
  margin-bottom: 0;
}

.detail-redirect {
  height: 19px;
  color: var(--s-900);
}

.edit-icon-button {
  margin-top: 0;
}

.status-card {
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--s-100);
  border-radius: 0;
  margin-bottom: var(--space-normal);
  padding: 0 0 var(--space-normal);
}

.status-actions {
  display: grid;
  gap: var(--space-small);
  margin-bottom: 0;
}

.status-main-actions {
  display: grid;
  gap: var(--space-small);
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.status-main-actions ::v-deep .button {
  min-height: 2.75rem;
}

.status-main-actions ::v-deep .button:disabled {
  background: var(--s-100) !important;
  border-color: var(--s-100) !important;
  color: var(--s-400) !important;
  cursor: not-allowed;
}

.status-cancel-link {
  background: transparent;
  border: 0;
  color: var(--w-500);
  cursor: pointer;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  margin-top: var(--space-smaller);
  padding: 0;
  text-align: center;
  width: 100%;
}

.status-cancel-link--disabled,
.status-cancel-link:disabled {
  color: var(--s-400);
  cursor: not-allowed;
}

.detail-lines p {
  margin-bottom: var(--space-smaller);
}

.details-products {
  margin-bottom: var(--space-small);
}

.details-loading {
  display: flex;
  padding: var(--space-small) 0;
}

.detail-summary {
  align-items: center;
  border-bottom: 1px solid var(--s-100);
  border-top: 1px solid var(--s-100);
  display: grid;
  gap: var(--space-small);
  grid-template-columns: auto auto auto;
  justify-content: start;
  margin-bottom: var(--space-normal);
  padding: var(--space-small) 0;
}

.contact-id {
  color: var(--w-600);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
}

.contact-name {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
}

.contact-meta {
  color: var(--s-700);
  font-size: var(--font-size-small);
}

.opportunity-main-row {
  align-items: center;
  display: flex;
  gap: var(--space-small);
  justify-content: space-between;
  margin-bottom: var(--space-small);
}

.opportunity-main-name {
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-medium);
  margin: 0;
}

.opportunity-main-value {
  flex-shrink: 0;
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-semibold);
  margin: 0;
  white-space: nowrap;
}

.details-card--compact {
  .detail-summary {
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  .contact-name,
  .opportunity-main-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.owner-card {
  align-items: center;
  display: flex;
  gap: var(--space-normal);
  margin-top: var(--space-smaller);
}

.owner-avatar {
  border-radius: 50%;
  height: 2.6rem;
  object-fit: cover;
  width: 2.6rem;
}

.owner-avatar--fallback {
  align-items: center;
  background: var(--w-500);
  color: var(--white);
  display: inline-flex;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  justify-content: center;
}

.owner-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.owner-name {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  margin: 0;
}

.owner-email {
  color: var(--s-700);
  font-size: var(--font-size-mini);
  margin: 0;
}

.negotiation-form-summary {
  align-items: center;
  border-radius: var(--border-radius-normal);
  color: var(--s-800);
  cursor: pointer;
  display: flex;
  font-size: var(--font-size-small);
  gap: var(--space-small);
  line-height: 1.4;
  margin-top: var(--space-normal);
  padding: var(--space-smaller);
  text-align: left;
  width: 100%;

  &:hover {
    background: var(--s-50);
  }
}

.negotiation-form-icon {
  color: var(--w-700);
  flex-shrink: 0;
}

@media (max-width: 1200px) {
  .status-actions {
    grid-template-columns: 1fr;
  }

  .status-main-actions {
    grid-template-columns: 1fr;
  }

  .detail-summary {
    grid-template-columns: 1fr;
  }
}
</style>
